import { useState } from 'react';
import { Button, Flex, Text, TextInput } from '@mantine/core';
import { BasicMantineTable } from '@dealstack/frontend/shared/components/BasicMantineTable/BasicMantineTable';
import { Icon } from '@dealstack/frontend/shared/components/fundament/Icon/Icon';
import { StyledSettingsContainer } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ProgrammeSettings/MasterDocuments/styles';
import { useTranslationOrganization } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import type { ExtendedColumnDef } from '@dealstack/frontend/shared/components/Table/MantineTable/useMantineTableWithPaginationFilteringSorting';
import type { OrganizationDealClassifierRes } from '@dealstack/shared/lib/backendClient';
import { useDealDefaultsClassifiers } from '@dealstack/frontend/app/pages/Organization/OrganizationDealDefaults/DealDefaultsClassifiers/useDealDefaultsClassifiers';

interface Props {
	organizationId: string;
}

export const DealDefaultsClassifiers = ({ organizationId }: Props) => {
	const tOrganization = useTranslationOrganization();
	const [newName, setNewName] = useState('');

	const { classifiers, isLoading, setClassifiers, isSaving } =
		useDealDefaultsClassifiers(organizationId);

	const names = classifiers.map((classifier) => classifier.name);

	const addClassifier = () => {
		const trimmed = newName.trim();

		if (!trimmed || names.includes(trimmed)) {
			return;
		}

		setClassifiers([...names, trimmed]);
		setNewName('');
	};

	const columns: ExtendedColumnDef<OrganizationDealClassifierRes>[] = [
		{
			accessorKey: 'name',
			header: tOrganization('deal_defaults.classifiers.table.name'),
			cell: ({ row }) => <Text size="B6Medium">{row.original.name}</Text>,
		},
		{
			id: 'actions',
			header: '',
			size: 200,
			minSize: 200,
			cell: ({ row }) => (
				<Flex justify="flex-end">
					<Button
						variant="subtle"
						color="red"
						style={{ flexShrink: 0 }}
						disabled={isSaving}
						onClick={() =>
							setClassifiers(names.filter((name) => name !== row.original.name))
						}
					>
						{tOrganization('deal_defaults.classifiers.remove')}
					</Button>
				</Flex>
			),
		},
	];

	return (
		<StyledSettingsContainer>
			<Flex direction="column" w="100%" gap="1rem">
				<Text size="B6Medium" c="gray.9">
					{tOrganization('deal_defaults.classifiers.description')}
				</Text>

				<Flex gap="0.5rem" align="flex-end">
					<TextInput
						w="20rem"
						label={tOrganization('deal_defaults.classifiers.add_label')}
						placeholder={tOrganization('deal_defaults.classifiers.add_placeholder')}
						value={newName}
						disabled={isSaving}
						onChange={(event) => setNewName(event.currentTarget.value)}
						onKeyDown={(event) => event.key === 'Enter' && addClassifier()}
					/>
					<Button
						leftSection={<Icon variant="add" size="lg" />}
						disabled={isSaving || !newName.trim()}
						onClick={addClassifier}
					>
						{tOrganization('deal_defaults.classifiers.add')}
					</Button>
				</Flex>

				<BasicMantineTable columns={columns} data={classifiers} isLoading={isLoading} />
			</Flex>
		</StyledSettingsContainer>
	);
};
